<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="900px"
    content-class="modern-award-dialog"
    transition="dialog-transition"
  >
    <v-card class="award-card">
      <!-- Enhanced Image Header with Gradient Overlay -->
      <div class="dialog-image-header">
        <div class="background-pattern"></div>
        <img src="@/assets/coupon.jpg" alt="Award Background" class="header-image">
        <div class="gradient-overlay"></div>
        <div class="header-content">
          <div class="header-icon">
            <v-icon size="48" color="white">mdi-trophy</v-icon>
          </div>
          <h2 class="header-title">Edit Award</h2>
          <p class="header-subtitle">Update the reward details for your volunteers</p>
          <div class="header-decoration">
            <div class="decoration-line"></div>
            <v-icon color="white" size="20">mdi-star</v-icon>
            <div class="decoration-line"></div>
          </div>
        </div>
        <v-btn 
          icon 
          @click="onClose" 
          class="close-btn"
          elevation="2"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
      
      <!-- Enhanced Floating Form Container -->
      <div class="floating-form-wrapper">
        <v-card class="form-card" elevation="12">
          <v-form ref="form" class="award-form">
            <!-- Award Title with Enhanced Styling -->
            <div class="form-section">
              <div class="section-header">
                <v-icon color="primary" class="section-icon">mdi-trophy-variant</v-icon>
                <h3 class="section-title">Award Details</h3>
              </div>
              
              <div class="form-field">
                <v-text-field
                  v-model="award.title"
                  label="Award Title"
                  outlined
                  color="primary"
                  prepend-inner-icon="mdi-trophy"
                  class="modern-input"
                  :rules="[rules.required]"
                  hide-details="auto"
                  placeholder="e.g., Outstanding Volunteer Recognition"
                ></v-text-field>
              </div>
            </div>

            <!-- Goals & Timing Section -->
            <div class="form-section">
              <div class="section-header">
                <v-icon color="success" class="section-icon">mdi-target</v-icon>
                <h3 class="section-title">Goals & Timeline</h3>
              </div>
              
              <v-row class="form-row">
                <v-col cols="12" sm="6">
                  <div class="form-field">
                    <v-text-field
                      v-model="award.goal_hours"
                      label="Required Hours"
                      type="number"
                      min="0"
                      outlined
                      color="primary"
                      prepend-inner-icon="mdi-clock-outline"
                      class="modern-input"
                      :rules="[rules.required, rules.positiveNumber]"
                      hide-details="auto"
                      placeholder="Enter minimum hours"
                      @input="award.goal_hours = award.goal_hours.toString().replace(/[^0-9]/g, '')"
                    ></v-text-field>
                  </div>
                </v-col>
                
                <v-col cols="12" sm="6">
                  <div class="form-field">
                    <v-menu
                      v-model="date_picker"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="award.expiryDate"
                          label="Expiry Date"
                          prepend-inner-icon="mdi-calendar-outline"
                          readonly
                          outlined
                          v-bind="attrs"
                          v-on="on"
                          color="primary"
                          class="modern-input"
                          hide-details="auto"
                          placeholder="Select expiry date"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="awardDate"
                        color="primary"
                        :min="new Date().toISOString().substr(0, 10)"
                        @input="onSaveDate"
                        elevation="8"
                      ></v-date-picker>
                    </v-menu>
                  </div>
                </v-col>
              </v-row>
            </div>
            
            <!-- Description Section -->
            <div class="form-section">
              <div class="section-header">
                <v-icon color="info" class="section-icon">mdi-text-box-outline</v-icon>
                <h3 class="section-title">Terms & Conditions</h3>
              </div>
              
              <div class="form-field">
                <v-textarea
                  v-model="award.description"
                  label="Award Description & Terms"
                  outlined
                  color="primary"
                  rows="4"
                  auto-grow
                  prepend-inner-icon="mdi-text-box-outline"
                  class="modern-input"
                  :rules="[rules.required]"
                  hide-details="auto"
                  placeholder="Describe the award criteria and terms..."
                ></v-textarea>
              </div>
            </div>
            
            <!-- Coupon Details Section -->
            <div class="form-section">
              <div class="section-header">
                <v-icon color="warning" class="section-icon">mdi-ticket-percent</v-icon>
                <h3 class="section-title">Coupon Configuration</h3>
              </div>
              
              <div class="form-field">
                <v-text-field
                  v-model="award.count"
                  label="Available Quantity"
                  type="number"
                  min="0"
                  outlined
                  color="primary"
                  prepend-inner-icon="mdi-numeric"
                  class="modern-input"
                  :rules="[rules.required, rules.positiveNumber]"
                  hide-details="auto"
                  placeholder="Total coupons available"
                  @input="award.count = award.count.toString().replace(/[^0-9]/g, '')"
                ></v-text-field>
              </div>
            </div>
            
            <!-- Enhanced Error Message -->
            <v-expand-transition>
              <v-alert
                v-if="message"
                type="error"
                class="error-alert"
                elevation="2"
                border="left"
                colored-border
                icon="mdi-alert-circle"
              >
                {{ message }}
              </v-alert>
            </v-expand-transition>
          </v-form>
        </v-card>
      </div>
      
      <!-- Enhanced Action Bar -->
      <div class="action-container">
        <div class="action-bar">
          <v-btn
            @click="onClose"
            outlined
            large
            class="cancel-btn"
            :disabled="loading"
          >
            <v-icon left>mdi-close</v-icon>
            Cancel
          </v-btn>
          
          <v-btn
            @click="onSave"
            color="primary"
            large
            class="save-btn"
            :loading="loading"
            :disabled="loading"
            elevation="4"
          >
            <v-icon left>mdi-check-circle</v-icon>
            Save Changes
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from "vuex";
import dateFormat from "dateformat";

export default {
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    item: {
      type: Object,
      default: () => ({})
    },
    onClose: {
      type: Function
    }
  },
  data() {
    return {
      message: null,
      loading: false,
      rules: {
        required: value => !!value || "This field is required",
        positiveNumber: value => value > 0 || "Must be greater than 0"
      },
      award: {},
      date_picker: false,
      awardDate: null
    };
  },
  methods: {
    ...mapActions("awards", {
      editAward: "editAward"
    }),
    
    onSave() {
      if (this.$refs.form.validate()) {
        const params = {
          _id: this.item._id
        };
        
        // Only include changed fields
        if (this.award.title !== this.item.title) params.title = this.award.title;
        if (this.award.goal_hours !== this.item.goal_hours) params.goal_hours = this.award.goal_hours;
        if (this.award.count !== this.item.count) params.count = this.award.count;
        if (this.award.expiryDate !== this.item.expiryDate) params.expiryDate = this.award.expiryDate;
        if (this.award.description !== this.item.description) params.description = this.award.description;

        this.loading = true;
        this.editAward(params)
          .then(() => {
            this.loading = false;
            this.onClose();
          })
          .catch(error => {
            this.loading = false;
            this.message = error.response?.data?.message || "Failed to update award";
          });
      }
    },
    
    onSaveDate(date) {
      this.award.expiryDate = this.changeDateFormat(date);
      this.date_picker = false;
    },
    
    changeDateFormat(dateStr) {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    },
    
    convertTZ(date, tzString) {
      if (!date) return "";
      return new Date(
        (typeof date === "string" ? new Date(date) : date).toLocaleString(
          "en-US",
          {
            timeZone: tzString ? tzString : "America/New_York"
          }
        )
      );
    }
  },
  mounted() {
    this.award = { ...this.item };
    const convertedDate = this.convertTZ(this.award.expiryDate);
    this.award.expiryDate = dateFormat(convertedDate, "mm/dd/yyyy");
    this.awardDate = convertedDate.toISOString().substr(0, 10);
  }
};
</script>

<style scoped>
/* Enhanced Dialog Styles */
.modern-award-dialog {
  border-radius: 34px;
  overflow: hidden;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.2);
}

.award-card {
  position: relative;
  overflow: visible;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

/* Enhanced Image Header */
.dialog-image-header {
  position: relative;
  height: 320px;
  overflow: hidden;
  background: linear-gradient(135deg, #084468, #0a5b8a);
}

.background-pattern {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 0%, transparent 50%),
    radial-gradient(circle at 75% 75%, rgba(255,255,255,0.05) 0%, transparent 50%);
  z-index: 1;
}

.header-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: brightness(0.7) contrast(1.1);
  opacity: 0.8;
}

.gradient-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background:linear-gradient(135deg, #084368cc, #0a5b8a32);
  z-index: 2;
}

.header-content {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: white;
  z-index: 3;
  width: 90%;
}

.header-icon {
  margin-bottom: 16px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

.header-title {
  font-size: 32px;
  font-weight: 800;
  margin-bottom: 8px;
  text-shadow: 0 4px 8px rgba(0,0,0,0.3);
  letter-spacing: -0.5px;
}

.header-subtitle {
  font-size: 18px;
  opacity: 0.95;
  font-weight: 400;
  text-shadow: 0 2px 4px rgba(0,0,0,0.2);
  margin-bottom: 20px;
}

.header-decoration {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.decoration-line {
  width: 40px;
  height: 1px;
  background: rgba(255,255,255,0.6);
}

.close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 4;
  background: rgba(255,255,255,0.15) !important;
  backdrop-filter: blur(10px);
  color: white !important;
  transition: all 0.3s ease;
}

.close-btn:hover {
  background: rgba(255,255,255,0.25) !important;
  transform: rotate(90deg);
}

/* Enhanced Form Container */
.floating-form-wrapper {
  position: relative;
  margin-top: -60px;
  z-index: 10;
  padding: 0 24px 0;
}

.form-card {
  border-radius: 20px;
  overflow: hidden;
  background: white;
  backdrop-filter: blur(10px);
}

.award-form {
  padding: 32px;
}

/* Form Sections */
.form-section {
  margin-bottom: 32px;
}

.form-section:last-of-type {
  margin-bottom: 0;
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f0f4f8;
}

.section-icon {
  margin-right: 12px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.form-row {
  margin: 0 -8px;
}

.form-row .v-col {
  padding: 0 8px;
}

.form-field {
  margin-bottom: 16px;
}

/* Enhanced Input Styles */
.modern-input::v-deep .v-input__slot {
  background: #f8fafc !important;
  border-radius: 12px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-height: 56px;
}

.modern-input::v-deep .v-input__slot:hover {
  border-color: #cbd5e0 !important;
  background: #f1f5f9 !important;
}

.modern-input::v-deep .v-input--is-focused .v-input__slot {
  border-color: #667eea !important;
  background: white !important;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
}

.modern-input::v-deep .v-icon {
  color: #667eea !important;
}

.modern-input::v-deep .v-label {
  color: #4a5568 !important;
  font-weight: 500;
  font-size: 14px;
}

.modern-input::v-deep .v-label--active {
  color: #667eea !important;
}

.modern-input::v-deep input,
.modern-input::v-deep textarea {
  font-size: 16px;
  color: #2d3748;
}

/* Error Alert */
.error-alert {
  margin-top: 20px;
  border-radius: 12px;
}

/* Enhanced Action Container */
.action-container {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 24px;
  border-top: 1px solid #e2e8f0;
}

.action-bar {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
}

.cancel-btn {
  height: 48px;
  border-radius: 24px;
  font-weight: 600;
  text-transform: none;
  border: 2px solid #e2e8f0;
  color: #64748b;
  transition: all 0.3s ease;
}

.cancel-btn:hover {
  border-color: #cbd5e0;
  background: #f8fafc;
}

.save-btn {
  height: 48px;
  border-radius: 24px;
  font-weight: 600;
  text-transform: none;
  padding: 0 32px;
  background: linear-gradient(135deg, #084468, #0a5b8a) !important;
  color: white !important;
  transition: all 0.3s ease;
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3) !important;
}

.save-btn:active {
  transform: translateY(0);
}

/* Responsive Design */
@media (max-width: 960px) {
  .floating-form-wrapper {
    padding: 0 16px 0;
    margin-top: -40px;
  }
  
  .award-form {
    padding: 24px;
  }
  
  .header-title {
    font-size: 28px;
  }
  
  .header-subtitle {
    font-size: 16px;
  }
}

@media (max-width: 600px) {
  .modern-award-dialog {
    margin: 0;
    max-height: 100vh;
    height: 100vh;
    border-radius: 0;
  }
  
  .dialog-image-header {
    height: 200px;
  }
  
  .header-title {
    font-size: 24px;
  }
  
  .header-subtitle {
    font-size: 14px;
  }
  
  .floating-form-wrapper {
    padding: 0 12px 0;
    margin-top: -30px;
  }
  
  .award-form {
    padding: 20px;
  }
  
  .section-title {
    font-size: 16px;
  }
  
  .action-bar {
    flex-direction: column-reverse;
    gap: 12px;
  }
  
  .cancel-btn,
  .save-btn {
    width: 100%;
  }
  
  .form-row {
    margin: 0;
  }
  
  .form-row .v-col {
    padding: 0;
  }
}

@media (max-width: 400px) {
  .dialog-image-header {
    height: 160px;
  }
  
  .header-icon {
    margin-bottom: 12px;
  }
  
  .header-icon .v-icon {
    font-size: 36px !important;
  }
  
  .header-title {
    font-size: 20px;
  }
}

/* Enhanced Animations */
.dialog-transition-enter-active,
.dialog-transition-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.dialog-transition-enter,
.dialog-transition-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

/* Loading States */
.save-btn.v-btn--loading {
  pointer-events: none;
}

.save-btn.v-btn--loading::v-deep .v-btn__content {
  opacity: 0;
}

/* Focus States for Accessibility */
.modern-input::v-deep .v-input--is-focused .v-input__slot {
  outline: none;
}

.close-btn:focus,
.cancel-btn:focus,
.save-btn:focus {
  outline: 2px solid #667eea;
  outline-offset: 2px;
}
</style>